import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { COMPANY_INFO, SITE_IMAGES, getCdnImage } from '../data/content';

export function Kontakt() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    userType: 'Privat',
    message: '',
    website_url: '', // Honeypot field for antispam
  });

  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });

    // Client side basic validation
    if (!formData.name || !formData.phone || !formData.email || !formData.message) {
      setStatus({
        loading: false,
        success: false,
        error: 'Udfyld venligst alle påkrævede felter (Navn, Telefon, E-mail og Beskrivelse).',
      });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          userType: formData.userType,
          message: formData.message,
          website_url: formData.website_url, // Honeypot
          subject: `Henvendelse vedr. SYSTEM 3E (${formData.userType})`,
        }),
      });

      if (response.ok) {
        setStatus({ loading: false, success: true, error: null });
        setFormData({
          name: '',
          phone: '',
          email: '',
          userType: 'Privat',
          message: '',
          website_url: '',
        });
      } else {
        const data = await response.json().catch(() => ({}));
        setStatus({
          loading: false,
          success: false,
          error: data.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte.',
        });
      }
    } catch (err) {
      console.error('Fejl ved indsendelse:', err);
      setStatus({
        loading: false,
        success: false,
        error: `Netværksfejl. Kontrollér din forbindelse eller ring direkte til os på ${COMPANY_INFO.phone}.`,
      });
    }
  };

  return (
    <>
      <SEO
        title="Kontakt os – AGT Nordic Group ApS"
        description={`Kontakt den officielle danske eneforhandler af SYSTEM 3E, AGT Nordic Group ApS i Vordingborg. Ring ${COMPANY_INFO.phone} eller send os en besked om dit byggeprojekt.`}
      />

      {/* Hero */}
      <section className="bg-[#243447] text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getCdnImage(SITE_IMAGES.calculatorHeader, 1600, 80)}
            alt="Kontakt AGT Nordic Group"
            className="w-full h-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-[#162332]/50" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="text-[#819FA8] font-extrabold text-xs uppercase tracking-widest block mb-2">
              Direkte Kontakt
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight mb-4">
              Kontakt AGT Nordic Group ApS
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Eneforhandler af SYSTEM 3E i Danmark. Vi rådgiver både private bygherrer, håndværkere og professionelle entreprenører om beregning, levering og montage.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Col: Contact Cards (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
                <div className="inline-flex items-center gap-2 bg-[#122a4e]/10 text-[#122a4e] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-6 border border-[#122a4e]/20">
                  <i className="fa-solid fa-certificate" aria-hidden="true" />
                  <span>Officiel Eneforhandler i Danmark</span>
                </div>

                <h2 className="text-2xl font-black text-slate-900 mb-6">{COMPANY_INFO.name}</h2>

                <div className="space-y-6 text-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#122a4e]/10 text-[#122a4e] rounded-xl flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-xs uppercase tracking-wider text-slate-500 mb-0.5">
                        Adresse
                      </p>
                      <p className="font-semibold text-slate-900">{COMPANY_INFO.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#122a4e]/10 text-[#122a4e] rounded-xl flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-xs uppercase tracking-wider text-slate-500 mb-0.5">
                        Telefon (Direkte)
                      </p>
                      <a
                        href={COMPANY_INFO.phoneRaw}
                        className="text-[#122a4e] hover:text-[#1d3b66] font-extrabold text-base transition-colors whitespace-nowrap block"
                      >
                        {COMPANY_INFO.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#122a4e]/10 text-[#122a4e] rounded-xl flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-xs uppercase tracking-wider text-slate-500 mb-0.5">
                        E-mail
                      </p>
                      <a
                        href={COMPANY_INFO.emailRaw}
                        className="text-[#122a4e] hover:text-[#1d3b66] font-extrabold text-base transition-colors whitespace-nowrap block"
                      >
                        {COMPANY_INFO.email}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Assurance Card */}
              <div className="bg-slate-900 text-white rounded-3xl p-8 border border-slate-800">
                <h3 className="font-bold text-lg text-white mb-2">Hvad kan vi hjælpe dig med?</h3>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#446ba9]" aria-hidden="true" />
                    <span>Elementberegning ud fra dine byggetegninger</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#446ba9]" aria-hidden="true" />
                    <span>Uforpligtende pristilbud og leveringsbetingelser</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#446ba9]" aria-hidden="true" />
                    <span>Rådgivning til professionelle håndværkere & arkitekter</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Col: Webform (7 cols) */}
            <div className="lg:col-span-7">
              <div className="bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 shadow-sm">
                <h2 className="text-2xl font-black text-slate-900 mb-2">
                  Send os en besked
                </h2>
                <p className="text-slate-600 text-sm mb-8">
                  Udfyld formularen herunder, så vender vi hurtigt tilbage til dig.
                </p>

                {status.success ? (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-2xl p-6 text-center space-y-3 animate-in fade-in duration-300">
                    <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xl mx-auto">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </div>
                    <h3 className="font-bold text-lg">Tak for din henvendelse!</h3>
                    <p className="text-sm text-emerald-800">
                      Vi har modtaget din besked og kontakter dig hurtigst muligt.
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus({ loading: false, success: false, error: null })}
                      className="text-xs font-bold text-emerald-700 underline hover:text-emerald-900 mt-2"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Antispam Honeypot - ABSOLUTELY HIDDEN FROM HUMANS */}
                    <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                      <label htmlFor="website_url">Lad dette felt være tomt</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.website_url}
                        onChange={handleChange}
                      />
                    </div>

                    {status.error && (
                      <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-xs font-semibold">
                        <i className="fa-solid fa-circle-exclamation mr-1.5 text-red-600" aria-hidden="true" />
                        {status.error}
                      </div>
                    )}

                    {/* User Type Selection */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                        Jeg henvender mig som: <span className="text-red-600">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <label
                          className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-sm font-bold cursor-pointer transition-all ${
                            formData.userType === 'Privat'
                              ? 'bg-[#122a4e]/10 border-[#122a4e] text-[#122a4e] shadow-sm'
                              : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          <input
                            type="radio"
                            name="userType"
                            value="Privat"
                            checked={formData.userType === 'Privat'}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <i className="fa-solid fa-house" aria-hidden="true" />
                          <span>Privatbygherre</span>
                        </label>

                        <label
                          className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-sm font-bold cursor-pointer transition-all ${
                            formData.userType === 'Professionel'
                              ? 'bg-[#122a4e]/10 border-[#122a4e] text-[#122a4e] shadow-sm'
                              : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          <input
                            type="radio"
                            name="userType"
                            value="Professionel"
                            checked={formData.userType === 'Professionel'}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <i className="fa-solid fa-helmet-safety" aria-hidden="true" />
                          <span>Professionel / Fagmand</span>
                        </label>
                      </div>
                    </div>

                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-xs font-bold text-slate-800 mb-1">
                        Dit Navn <span className="text-[#122a4e]">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="f.eks. Peter Hansen"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#122a4e] focus:border-transparent text-sm bg-slate-50/50"
                      />
                    </div>

                    {/* Phone & Email */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="block text-xs font-bold text-slate-800 mb-1">
                          Telefonnummer <span className="text-[#122a4e]">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="f.eks. 12 34 56 78"
                          className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#122a4e] focus:border-transparent text-sm bg-slate-50/50"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-xs font-bold text-slate-800 mb-1">
                          E-mailadresse <span className="text-[#122a4e]">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="f.eks. navn@eksempel.dk"
                          className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#122a4e] focus:border-transparent text-sm bg-slate-50/50"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-xs font-bold text-slate-800 mb-1">
                        Beskrivelse af dit projekt eller spørgsmål <span className="text-[#122a4e]">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Fortæl kort om dit byggeprojekt, m² ydervæg, placering eller hvad du ønsker rådgivning om..."
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#122a4e] focus:border-transparent text-sm bg-slate-50/50"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={status.loading}
                      className="w-full bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold py-4 rounded-xl text-base transition-all shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {status.loading ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                          <span>Sender din besked...</span>
                        </>
                      ) : (
                        <>
                          <span>Send henvendelse</span>
                          <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
